// /admin → In progress — the operator's half of the issues loop.
//
//   inbox ──(a volunteer investigates, writes a note)──▶ progress   ← shown here
//   progress ──(the developer fixes it: Mark fixed)──▶ fixed
//   fixed ──(the volunteer re-opens the route, it works)──▶ archived
//
// Everything else about a report or a bad route — the inbox, the statuses, the
// notes, the archive — is on /volunteer/issues. This screen lists only what a
// volunteer has marked In Progress, with their note, and does one thing to it.
//
// Reads and writes through `/api/issues` (built with Volunteer Tools):
// `GET ?state=progress` for the list, `POST { op: "mark-fixed", key, devNote }`
// for the button — operator-only on the server. The optional reply is the
// DEVELOPER's `devNote`, written beside the volunteer's `note` and never over
// it: "the route goes through a locked door on Sundays" is the evidence, and
// "fixed the door, needs a snapshot" must not replace it. Marking fixed does
// not archive anything — the volunteer's re-check is the step that closes it.

import { useCallback, useEffect, useRef, useState } from 'react';
import { fmtTime, optionTags, destLabel } from '../utils/adminLabels.js';

export default function InProgressIssues({ apiBase, token, routeLink, onUnauthorized, onChanged }) {
  const [issues, setIssues] = useState(null);
  const [counts, setCounts] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState('');         // key whose reply box is open
  const [reply, setReply] = useState('');
  const unauthRef = useRef(onUnauthorized);
  unauthRef.current = onUnauthorized;

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${apiBase}/api/issues?state=progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 401) { unauthRef.current?.(); return; }
      if (r.status === 404 && !j.error) { setErr('The issues API (/api/issues) is not deployed here yet.'); setIssues([]); return; }
      if (!r.ok) { setErr(j.error || 'Could not load the issues in progress.'); setIssues([]); return; }
      setErr('');
      setIssues(Array.isArray(j.issues) ? j.issues : []);
      setCounts(j.counts || null);
    } catch { setErr('Network error.'); setIssues([]); }
  }, [apiBase, token]);

  useEffect(() => { load(); }, [load]);

  const markFixed = async (it) => {
    setBusy(it.key); setErr('');
    try {
      const r = await fetch(`${apiBase}/api/issues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          op: 'mark-fixed', key: it.key,
          ...(open === it.key && reply.trim() ? { devNote: reply.trim() } : {}),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.ok === false) { setErr(j.error || 'That did not save.'); return; }
      setOpen(''); setReply('');
      await load();
      onChanged?.();
    } catch { setErr('Network error.'); }
    finally { setBusy(''); }
  };

  return (
    <div>
      <div style={S.bar}>
        <div style={S.group}>
          <div style={S.groupLabel}>Triage lives in Volunteer Tools</div>
          <div style={S.groupButtons}>
            <a style={S.btnGhost} href="/volunteer/issues">Open Reports &amp; bad routes ↗</a>
            <a style={S.btnGhost} href="/volunteer/moderate">Open Event &amp; review moderation ↗</a>
          </div>
          <div style={S.groupNote}>
            Every report and bad route rating is worked on the volunteer side. Only the ones a volunteer marks
            In Progress come back here; once you mark one fixed, they re-check it and archive it.
          </div>
        </div>
        {counts && (
          <div style={S.group}>
            <div style={S.groupLabel}>Where things stand</div>
            <div style={S.countRow}>
              <span><b>{counts.inbox}</b> not checked</span>
              <span><b>{counts.progress}</b> in progress</span>
              <span><b>{counts.fixed}</b> fixed, awaiting re-check</span>
              <span><b>{counts.archived}</b> archived</span>
            </div>
          </div>
        )}
      </div>

      {err && <div style={S.err}>{err}</div>}

      <div style={S.list}>
        {issues === null && <div style={S.empty}>Loading…</div>}
        {issues && issues.length === 0 && !err && (
          <div style={S.empty}>Nothing in progress. When a volunteer marks an issue In Progress it appears here with their note.</div>
        )}
        {(issues || []).map((it) => {
          const link = routeLink({ from: it.from, to: it.to, options: it.options });
          const route = [it.from, destLabel(it.to)].filter(Boolean).join(' → ');
          const t = it.triage || {};
          const replying = open === it.key;
          return (
            <div key={it.key} style={S.item}>
              <div style={S.head}>
                <span style={{ ...S.kind, ...(it.kind === 'rating' ? S.kindRating : {}) }}>
                  {it.kind === 'rating' ? 'bad route' : 'report'}
                </span>
                <div style={S.main}>
                  {route && <div style={S.route}>{route}</div>}
                  <div style={S.meta}>
                    {[optionTags(it.options), it.device, it.source, `sent ${fmtTime(it.ts)}`]
                      .filter((x) => x && x !== '—').join(' · ')}
                  </div>
                </div>
                <div style={S.controls}>
                  {link && <a style={S.openRoute} href={link} target="_blank" rel="noreferrer">Open route ↗</a>}
                  <button style={S.reply} onClick={() => { setOpen(replying ? '' : it.key); setReply(replying ? '' : (t.devNote || '')); }}>
                    {replying ? '▾' : '▸'} Reply
                  </button>
                  <button style={S.fixed} disabled={busy === it.key} onClick={() => markFixed(it)}>
                    {busy === it.key ? 'Saving…' : 'Mark fixed'}
                  </button>
                </div>
              </div>
              {!!it.message && <div style={S.said}>“{it.message}”</div>}
              <div style={S.volNote}>
                <div style={S.noteLabel}>
                  Volunteer note{t.by ? ` · ${t.by}` : ''}{t.updatedAt ? ` · ${fmtTime(t.updatedAt)}` : ''}
                </div>
                <div style={S.noteText}>{t.note || <span style={S.dim}>No note written.</span>}</div>
              </div>
              {replying && (
                <div style={S.replyBox}>
                  <textarea style={S.textarea} rows={3} value={reply} onChange={(e) => setReply(e.target.value)}
                            placeholder="Optional — what you changed, for the volunteer who re-checks it. Saved with Mark fixed." />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const S = {
  bar: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start', margin: '0 0 12px' },
  group: { background: '#fff', border: '1px solid #dadce0', borderRadius: 12, padding: '9px 12px 11px', flex: '1 1 260px', minWidth: 0 },
  groupLabel: { fontSize: 11, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 },
  groupButtons: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  groupNote: { fontSize: 12, color: '#5f6368', lineHeight: 1.45, marginTop: 8 },
  countRow: { display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13, color: '#3c4043' },
  btnGhost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '7px 13px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', textDecoration: 'none' },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { background: '#fff', borderRadius: 12, padding: 16, fontSize: 14, color: '#5f6368' },
  item: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 14px', fontSize: 13, boxSizing: 'border-box' },
  kind: { borderRadius: 999, padding: '3px 0', width: 76, textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', fontSize: 10.5, letterSpacing: 0.4, flexShrink: 0, background: '#f1f3f4', color: '#3c4043' },
  kindRating: { background: '#fce8e6', color: '#c5221f' },
  main: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 },
  route: { color: '#202124', fontWeight: 600, lineHeight: 1.35, overflowWrap: 'anywhere' },
  meta: { color: '#5f6368', fontSize: 12, lineHeight: 1.35, overflowWrap: 'anywhere' },
  controls: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' },
  openRoute: { color: '#1a73e8', fontWeight: 700, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap', border: '1px solid #d2e3fc', borderRadius: 999, padding: '4px 10px', background: '#f8fbff' },
  reply: { background: 'none', border: '1px solid transparent', borderRadius: 999, padding: '4px 8px', font: 'inherit', fontSize: 12, fontWeight: 600, color: '#5f6368', cursor: 'pointer' },
  fixed: { background: '#188038', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  said: { fontSize: 14, color: '#3c4043', whiteSpace: 'pre-wrap', lineHeight: 1.4, padding: '0 14px 10px 100px' },
  volNote: { margin: '0 14px 12px 100px', padding: '8px 10px', background: '#fef7e0', border: '1px solid #fde293', borderRadius: 8 },
  noteLabel: { fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#5f6368', marginBottom: 4 },
  noteText: { fontSize: 13, color: '#3c4043', whiteSpace: 'pre-wrap', lineHeight: 1.45 },
  replyBox: { padding: '0 14px 12px 100px' },
  textarea: { width: '100%', boxSizing: 'border-box', border: '1px solid #dadce0', borderRadius: 8, padding: '8px 10px', font: 'inherit', fontSize: 13, lineHeight: 1.45, resize: 'vertical' },
  dim: { color: '#80868b' },
};
